/**
 * @file ErrorBoundary.jsx
 * @description React error boundary that catches unhandled render errors, logs them
 *   via errorService, and renders a fallback UI with an error reference number.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/ErrorBoundary
 */
import { Component } from "react";
import { useTranslation } from "react-i18next";
import { captureError } from "../lib/errorService";
import { T } from "../theme";
import { AlertTriangleIcon } from "./Icons";

function ErrorFallback({ errorId, errorMessage, errorStack }) {
  const { t } = useTranslation();
  return (
    <div style={{
      height: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: T.bg, fontFamily: "'Inter', sans-serif", padding: 32,
    }}>
      <div style={{
        maxWidth: 520, textAlign: "center",
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: T.radiusLg, padding: 40,
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      }}>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}><AlertTriangleIcon size={40} color={T.orange} /></div>
        <h2 style={{ margin: "0 0 8px", color: T.text, fontSize: 20, fontWeight: 700 }}>
          {t("common.errorBoundary.title")}
        </h2>
        <p style={{ margin: "0 0 16px", color: T.textMid, fontSize: 14, lineHeight: 1.6 }}>
          {t("common.errorBoundary.message")}
          {errorId && (
            <span style={{ display: "block", marginTop: 8, fontSize: 11, color: T.textLight, fontFamily: "monospace" }}>
              {t("common.errorBoundary.errorId", { id: errorId })}
            </span>
          )}
        </p>
        {errorMessage && (
          <div style={{
            marginBottom: 16, textAlign: "left",
            background: "#fef2f2", border: "1px solid #fecaca",
            borderRadius: 8, padding: "10px 14px",
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#b91c1c", marginBottom: 4 }}>Error</div>
            <div style={{ fontSize: 11, fontFamily: "monospace", color: "#7f1d1d", wordBreak: "break-all" }}>
              {errorMessage}
            </div>
            {errorStack && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ fontSize: 10, color: "#b91c1c", cursor: "pointer" }}>Stack trace</summary>
                <pre style={{ fontSize: 10, marginTop: 4, whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#7f1d1d" }}>
                  {errorStack.slice(0, 800)}
                </pre>
              </details>
            )}
          </div>
        )}
        <button
          onClick={() => window.location.reload()}
          style={{
            background: T.blue, color: "#fff", border: "none",
            borderRadius: T.radiusMd, padding: "10px 24px",
            fontSize: 14, fontWeight: 600, cursor: "pointer",
          }}
        >
          {t("common.errorBoundary.reload")}
        </button>
      </div>
    </div>
  );
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null, errorMessage: null, errorStack: null };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.message || String(error),
      errorStack: error?.stack || null,
    };
  }

  componentDidCatch(error, info) {
    const errorId = Date.now().toString(36);
    this.setState({ errorId });
    // info.componentStack — the JSX tree at the moment of the crash — was
    // previously only console.error'd, never persisted. For a minified
    // production bundle with no local source map, this is often the only
    // thing that can actually point at which component failed.
    captureError(error, "react_boundary", this.props.userId || null, {
      componentStack: info?.componentStack?.slice(0, 1500),
    });
    console.error("[ErrorBoundary]", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <ErrorFallback
        errorId={this.state.errorId}
        errorMessage={this.state.errorMessage}
        errorStack={this.state.errorStack}
      />
    );
  }
}
